import { getCurrentInstance } from 'vue'
import pinia from '@/common/pinia'
import { useGlobalStore } from '@/stores/global'

// The server adds the user from the session and finds the page from `path`. Do not send user or page fields.
export const ENDPOINT = '/api/activity_log'
export const MAX_BATCH = 20
export const FLUSH_DELAY_MS = 5000

const state = {
  queue: [],
  timer: null,
  installed: false,
  perPage: new Set()
}

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content
const currentPath = () => window.location.pathname
const actorToken = () => useGlobalStore(pinia).user?.activityActor ?? null
const ADMIN_PATH = /^\/admin(\/|$)/

// Only the signed-in layout fills the global user. Admin pages share that layout, so staff clicks there must not go into the customer log.
function enabled() {
  return Boolean(actorToken()) && !ADMIN_PATH.test(currentPath())
}

function scheduleFlush() {
  if (state.timer) return
  state.timer = setTimeout(flush, FLUSH_DELAY_MS)
}

function enqueue(event) {
  if (!enabled()) return
  state.queue.push({ ...event, t: performance.now() })
  if (state.queue.length >= MAX_BATCH) flush()
  else scheduleFlush()
}

const componentName = ({ type }) => type.displayName || type.name || type.__name || null

// Each script-setup SFC keeps its file name in a production build, so the host names an unnamed primitive.
// The vnode context is the component whose template wrote the primitive, also through slots and wrappers.
export function hostName() {
  return componentName(getCurrentInstance().vnode.ctx)
}

export function logPageView(path) {
  enqueue({ kind: 'page_view', path, via: 'push' })
}

export function logEntityView(name, { subjectId, via, context }) {
  enqueue({ kind: 'entity_view', name, via, subject_id: subjectId, context, path: currentPath() })
}

// An overlay that stays open across a route change passes `path` to name the page it opened on.
export function logUiEvent(name, { target = null, via = null, context = {}, path = currentPath() } = {}) {
  enqueue({ kind: 'ui_event', name, target, via, context, path })
}

export function oncePerPage(key) {
  if (state.perPage.has(key)) return false
  state.perPage.add(key)
  return true
}

export function resetPerPageState() {
  state.perPage.clear()
}

export function flush() {
  if (state.timer) {
    clearTimeout(state.timer)
    state.timer = null
  }
  if (!state.queue.length) return

  const now = performance.now()
  const events = state.queue.splice(0).map(({ t, ...event }) => ({ ...event, age_ms: Math.round(now - t) }))
  // keepalive is the one transport that survives pagehide and can still carry the CSRF header.
  fetch(ENDPOINT, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify({ actor: actorToken(), events })
  }).catch(() => {})
}

export function installActivityLog() {
  if (state.installed) return
  state.installed = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  window.addEventListener('pagehide', flush)
}
